import {
  Box, Card, CardContent, Typography, Button, Grid, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress, Divider, Chip,
} from '@mui/material';
import { Print, Today } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function PrintableSummary({ data, date, tenant }) {
  if (!data) return null;
  const { sales, expenses, returns, topProducts } = data;
  return (
    <Box sx={{ p: 4, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
      <style>{`@media print { @page { size: A4; margin: 15mm; } }`}</style>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{tenant?.name || 'Business Name'}</Typography>
        <Typography variant="h6">End of Day Report — {new Date(date + 'T00:00:00').toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          ['Total Sales', sales.count, 'transactions'],
          ['Revenue', `₨ ${fmt(sales.revenue)}`, ''],
          ['Cash Collected', `₨ ${fmt(sales.collected)}`, ''],
          ['Credit Given', `₨ ${fmt(sales.credit)}`, ''],
        ].map(([label, val, sub]) => (
          <Grid item xs={6} key={label}>
            <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h6" fontWeight={700}>{val}</Typography>
              {sub && <Typography variant="caption">{sub}</Typography>}
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
        <Typography fontWeight={600} mb={1}>Payment Breakdown</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="body2">Cash: ₨ {fmt(sales.cash_sales)}</Typography>
          <Typography variant="body2">Card: ₨ {fmt(sales.card_sales)}</Typography>
          <Typography variant="body2">Bank: ₨ {fmt(sales.bank_sales)}</Typography>
        </Box>
      </Box>
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
        <Typography fontWeight={600} mb={1}>Expenses: ₨ {fmt(expenses.amount)} ({expenses.count} entries)</Typography>
        <Typography variant="body2" color="error.main">Net Cash: ₨ {fmt(sales.cash_sales - expenses.amount)}</Typography>
      </Box>
      {topProducts?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={600} mb={1}>Top 5 Products</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Revenue</TableCell></TableRow></TableHead>
            <TableBody>
              {topProducts.map((p, i) => (
                <TableRow key={i}><TableCell>{p.name}</TableCell><TableCell align="right">{p.qty_sold}</TableCell><TableCell align="right">₨ {fmt(p.revenue)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center' }}>
        <Typography variant="caption">Generated: {new Date().toLocaleString('en-PK')} | {tenant?.name}</Typography>
      </Box>
    </Box>
  );
}

export default function EODReportPage() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([api.get('/reports/eod', { params: { date } }), api.get('/settings/business')]);
      setData(r.data.data); setTenant(t.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [date]);

  const { sales, expenses, returns, topProducts, hourly } = data || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Today color="primary" />
          <Typography variant="h5" fontWeight={700}>End-of-Day Report</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }} />
          <Button variant="contained" startIcon={<Print />} onClick={handlePrint} disabled={!data}>Print Z-Report</Button>
        </Box>
      </Box>

      {loading ? <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box> : data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Sales', value: `₨ ${fmt(sales.revenue)}`, sub: `${sales.count} transactions`, color: 'success' },
              { label: 'Cash Collected', value: `₨ ${fmt(sales.collected)}`, sub: `Credit: ₨ ${fmt(sales.credit)}`, color: 'primary' },
              { label: 'Discounts Given', value: `₨ ${fmt(sales.discounts)}`, sub: `Tax: ₨ ${fmt(sales.tax)}`, color: 'warning' },
              { label: 'Expenses', value: `₨ ${fmt(expenses.amount)}`, sub: `${expenses.count} entries`, color: 'error' },
            ].map(c => (
              <Grid item xs={6} md={3} key={c.label}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                    <Typography variant="h5" fontWeight={700} color={`${c.color}.main`}>{c.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Payment breakdown */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography fontWeight={600} mb={2}>Payment Methods</Typography>
                  {[['Cash', sales.cash_sales, 'success'], ['Card', sales.card_sales, 'info'], ['Bank Transfer', sales.bank_sales, 'primary']].map(([label, val, color]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography>{label}</Typography>
                      <Chip label={`₨ ${fmt(val)}`} size="small" color={color} />
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                    <Typography fontWeight={700}>Net Cash</Typography>
                    <Typography fontWeight={700} color="success.main">₨ {fmt(+sales.cash_sales - +expenses.amount)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Hourly chart */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography fontWeight={600} mb={2}>Sales by Hour</Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={(hourly || []).map(h => ({ hour: `${h.hour}:00`, revenue: +h.revenue, count: h.count }))}>
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                      <Bar dataKey="revenue" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top products */}
          {topProducts?.length > 0 && (
            <Card>
              <CardContent>
                <Typography fontWeight={600} mb={2}>Top Selling Products</Typography>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty Sold</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {topProducts.map((p, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{p.name}</TableCell>
                        <TableCell align="right">{p.qty_sold}</TableCell>
                        <TableCell align="right">₨ {fmt(p.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Hidden print area */}
      <Box sx={{ display: 'none' }}>
        <Box ref={printRef}><PrintableSummary data={data} date={date} tenant={tenant} /></Box>
      </Box>
    </Box>
  );
}
